import React, { useCallback, useEffect, useState } from "react";
import api from "@/utils/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { ShieldAlert, Check, X, Lock } from "lucide-react";

// Aug 2026 — Bestands-Integritätsprüfung: Belege, die gegen aktualisierte
// Steuerregeln verstoßen, mit Ein-Klick-Korrektur oder "So lassen".
export const IntegrityReviewBanner = ({ onChanged }) => {
  const [items, setItems] = useState([]);
  const [busyId, setBusyId] = useState(null);
  const [expanded, setExpanded] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/expenses/integrity-review");
      setItems(r.data?.items || []);
    } catch {
      setItems([]);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const resolve = async (id, action) => {
    setBusyId(id);
    try {
      await api.post(`/expenses/${id}/integrity-resolve`, { action });
      toast.success(action === "apply" ? "Korrektur übernommen" : "Beleg unverändert belassen");
      await load();
      onChanged && onChanged();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Aktion fehlgeschlagen");
    } finally {
      setBusyId(null);
    }
  };

  if (!items.length) return null;

  return (
    <div className="mb-4 rounded-lg border border-amber-300 bg-amber-50 p-4" data-testid="integrity-review-banner">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-amber-900">
          <ShieldAlert className="h-5 w-5 shrink-0" />
          <span className="text-sm font-semibold">
            {items.length} Beleg{items.length > 1 ? "e" : ""} sollte{items.length > 1 ? "n" : ""} geprüft werden (aktualisierte Steuerregeln)
          </span>
        </div>
        <Button size="sm" variant="outline" data-testid="integrity-toggle-btn" onClick={() => setExpanded((v) => !v)}>
          {expanded ? "Einklappen" : "Prüfen"}
        </Button>
      </div>
      {expanded && (
        <div className="mt-3 space-y-2">
          {items.map((it) => (
            <div key={it.id} className="rounded-md border border-amber-200 bg-white p-3" data-testid={`integrity-item-${it.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="text-sm font-medium text-slate-800 truncate">
                    {it.description || it.vendor || "Beleg"}
                    <span className="ml-2 text-xs text-slate-500 tabular-nums">
                      {String(it.date || "").slice(0, 10)} · {Number(it.amount || 0).toLocaleString("de-DE", { minimumFractionDigits: 2 })} €
                    </span>
                  </div>
                  <div className="text-xs text-slate-600 mt-1 leading-snug">{it.integrity_reason}</div>
                  {it.is_locked && (
                    <div className="text-[11px] text-red-700 mt-1 flex items-center gap-1">
                      <Lock className="h-3 w-3" /> Periode festgeschrieben — Korrektur gehört als Berichtigung in die nächste UVA.
                    </div>
                  )}
                </div>
                <div className="flex gap-2 shrink-0">
                  <Button
                    size="sm"
                    disabled={busyId === it.id || it.is_locked}
                    data-testid={`integrity-apply-${it.id}`}
                    onClick={() => resolve(it.id, "apply")}
                  >
                    <Check className="h-4 w-4 mr-1" /> Korrektur übernehmen
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={busyId === it.id}
                    data-testid={`integrity-dismiss-${it.id}`}
                    onClick={() => resolve(it.id, "dismiss")}
                  >
                    <X className="h-4 w-4 mr-1" /> So lassen
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default IntegrityReviewBanner;
